import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, SectionHeader, PrimaryBtn, IconBtn, Field } from "./ui";

export default function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const empty = { name: "", category: "Kits pedagogiques", description: "", price: 0, lookup_key: "", image: "", stock: 50 };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => { api.get("/products").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { name: form.name, category: form.category, description: form.description, price: parseFloat(form.price) || 0, lookup_key: form.lookup_key, image: form.image, stock: parseInt(form.stock, 10) || 0 };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (p) => { if (!window.confirm(`Supprimer "${p.name}" ?`)) return; await api.delete(`/products/${p.id}`); toast.success("Produit supprimé"); load(); };

  return (
    <Card>
      <SectionHeader title="Produits" action={<PrimaryBtn onClick={openNew} data-testid="admin-new-product"><Plus className="w-4 h-4" /> Nouveau produit</PrimaryBtn>} />
      <div className="space-y-3" data-testid="admin-products-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 border border-slate-100 rounded-2xl p-4">
            <img src={p.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900 truncate">{p.name}</p>
              <p className="text-xs text-slate-400">{p.category} · {p.price.toFixed(2)} €</p>
            </div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${p.stock > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid={`admin-stock-${p.id}`}>
              Stock : {p.stock}
            </span>
            <IconBtn icon={Pencil} color="sky" onClick={() => openEdit(p)} testid={`admin-edit-product-${p.id}`} />
            <IconBtn icon={Trash2} color="rose" onClick={() => remove(p)} testid={`admin-delete-product-${p.id}`} />
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="admin-product-form">
            <Field label="Nom"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="pf-name" /></Field>
            <Field label="Catégorie">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Kits pédagogiques">Kits pédagogiques</SelectItem><SelectItem value="Goodies">Goodies</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Description"><Textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="pf-description" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Prix (EUR)"><Input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-xl mt-1" data-testid="pf-price" /></Field>
              <Field label="Stock"><Input required type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-xl mt-1" data-testid="pf-stock" /></Field>
            </div>
            <Field label="Clé Stripe (lookup_key)"><Input required value={form.lookup_key} onChange={(e) => setForm({ ...form, lookup_key: e.target.value })} className="rounded-xl mt-1" data-testid="pf-lookup" /></Field>
            <Field label="Image (URL)"><Input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-xl mt-1" data-testid="pf-image" /></Field>
            <PrimaryBtn type="submit" data-testid="pf-submit">{editing ? "Enregistrer" : "Creer"}</PrimaryBtn>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
